/**
 * Per-person display preferences.
 *
 * <h2>Why these live in the browser and not the database</h2>
 * Nothing here changes what the server does or what anyone else sees. A page
 * size is a convenience for one person at one desk, and putting it in Oracle
 * would mean a table, a migration, an endpoint and a round trip on every page
 * load to answer a question the browser already knows.
 *
 * That reasoning stops the moment a preference has consequences — which
 * notifications you receive, say, or an approval delegation. Those belong to
 * the account, not the browser, and they belong on the server.
 *
 * <h2>Storage can always fail</h2>
 * A private window, blocked site data, or a full quota all make these calls
 * throw. Every access is wrapped and falls back to the default, because
 * failing to remember a page size must never be the reason a screen does not
 * render.
 */

const KEY = 'sipay.preferences'

export const PAGE_SIZES = [10, 20, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]

/** 'system' follows the operating system and changes with it; the others pin one look. */
export const THEMES = ['system', 'light', 'dark'] as const
export type ThemePreference = (typeof THEMES)[number]

export interface Preferences {
  /** Rows per page in document lists. */
  pageSize: PageSize
  /** Branch pre-selected on a new document; '' means "ask every time". */
  defaultBranchId: string
  theme: ThemePreference
}

export const DEFAULT_PREFERENCES: Preferences = {
  pageSize: 20,
  defaultBranchId: '',
  theme: 'system',
}

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/**
 * Values arriving from storage are untrusted: they were written by an older
 * build, or edited by hand. Anything unrecognised falls back to the default
 * rather than reaching a component as a page size of "banana".
 */
export function parsePreferences(raw: string | null): Preferences {
  if (!raw) return DEFAULT_PREFERENCES
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return DEFAULT_PREFERENCES
  }
  if (typeof parsed !== 'object' || parsed === null) return DEFAULT_PREFERENCES

  const candidate = parsed as Partial<Record<keyof Preferences, unknown>>
  const pageSize = PAGE_SIZES.find((size) => size === candidate.pageSize)
  const branch = typeof candidate.defaultBranchId === 'string' ? candidate.defaultBranchId : ''
  const theme = THEMES.find((value) => value === candidate.theme)

  return {
    pageSize: pageSize ?? DEFAULT_PREFERENCES.pageSize,
    defaultBranchId: branch,
    theme: theme ?? DEFAULT_PREFERENCES.theme,
  }
}

export interface PreferenceStore {
  read(): Preferences
  write(next: Preferences): void
  reset(): void
}

export function createPreferenceStore(storage: KeyValueStore | null): PreferenceStore {
  return {
    read() {
      try {
        return parsePreferences(storage?.getItem(KEY) ?? null)
      } catch {
        return DEFAULT_PREFERENCES
      }
    },
    write(next: Preferences) {
      try {
        storage?.setItem(KEY, JSON.stringify(next))
      } catch {
        // Not worth failing a click over.
      }
    },
    reset() {
      try {
        storage?.removeItem(KEY)
      } catch {
        /* see above */
      }
    },
  }
}

function browserStorage(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export const preferences = createPreferenceStore(browserStorage())
